import { ClipboardCheck, ClipboardCopy } from "lucide-react"
import React from "react"
import shadow from "react-shadow"
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter"
import rust from "react-syntax-highlighter/dist/esm/languages/prism/rust"
import toml from "react-syntax-highlighter/dist/esm/languages/prism/toml"
import oneDark from "react-syntax-highlighter/dist/esm/styles/prism/one-dark"
import oneLight from "react-syntax-highlighter/dist/esm/styles/prism/one-light"

import { Theme, useTheme } from "@/hooks/useTheme"

type CodeBlockProps = {
  language: string
  text?: string
  filename?: string
  children?: string
}

// Register available languages

SyntaxHighlighter.registerLanguage("rust", rust)
SyntaxHighlighter.registerLanguage("toml", toml)

export default function CodeBlock({
  language,
  text,
  filename,
  children,
}: React.PropsWithoutRef<CodeBlockProps>) {
  const [copied, setCopied] = React.useState(false)
  const { theme } = useTheme()
  text = text || children
  const handleCopy = React.useCallback(() => {
    if (!text) return
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [text])

  if (!text) return null

  const Icon = copied ? ClipboardCheck : ClipboardCopy
  const styles = {
    [Theme.Dark]: oneDark,
    [Theme.Light]: oneLight,
  }

  return (
    <div className="not-prose mb-4">
      <button
        type="button"
        className="flex text-sm w-full text-left border-b bg-gray-100 dark:bg-gray-800 p-2 relative cursor-pointer"
        onClick={handleCopy}
      >
        <span className="text-zinc-500 dark:text-zinc-300 grow">
          {filename}
        </span>
        <span className="absolute w-10 mr-1 right-8">
          {copied ? "copied!" : ""}
        </span>
        <Icon className="h-5 w-5 text-zinc-500" />
      </button>
      {/* We use a shadow DOM to hide the "table" css class from tailwind from the syntax highlighter,
      which uses the "table" css class itself. */}
      <shadow.div className="-mt-2">
        <SyntaxHighlighter
          language={language}
          style={styles[theme] ?? oneLight}
        >
          {text}
        </SyntaxHighlighter>
      </shadow.div>
    </div>
  )
}
